import {
  Box,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, DragEvent, useState } from "react";
import { documentsAPI } from "../../apis";
import { USER_DOC_UPLOAD_TYPES, MAX_UPLOAD_BYTES } from "../../constants";
import { DocumentIcon } from "./DocumentIcon";

import { DocumentTypeFormControl } from "views/shared/forms/DocumentTypeFormControl";

interface Props {
  onComplete?: () => void;
  onCancel?: () => void;
  includeOnlyDocumentTypeIds?: number[],
}

export const CreateUserDocument = ({
  onComplete,
  onCancel,
  includeOnlyDocumentTypeIds,
}: Props): JSX.Element => {
  const isNew = true;
  const [title, setTitle] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [documentType, setDocumentType] = useState<number>(getInitialDocTypeId());
  const [uploadedFiles, setUploadedFiles] = useState<FileList | undefined>();
  const [progress, setProgress] = useState<number | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function getInitialDocTypeId():number {
    if(includeOnlyDocumentTypeIds && includeOnlyDocumentTypeIds.length > 0) {
      return includeOnlyDocumentTypeIds[0]
    }

    return 1 // "other"
  }

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    setProgress(undefined);
    const submitErrors: string[] = [];
    if (!title?.trim()) {
      submitErrors.push("Please enter a document title.");
    }
    const uploadFile = uploadedFiles?.item(0) || undefined;
    // if they selected/dragged a file it will be in upload file
    if (isNew) {
      if (!uploadFile) {
        submitErrors.push("Please choose a document to upload.");
      }
    }
    if (submitErrors?.length) {
      setErrors(submitErrors);
      setIsSubmitting(false);
      return;
    }
    // TODO: potentially enable multiple uploads
    const result = await documentsAPI.saveDocument(
      title,
      undefined,
      documentType,
      uploadFile,
      notes,
      undefined,
      (percent) => setProgress(percent), // callback to display progress,
      true
    );
    setProgress(undefined);
    if (result) {
      setIsSubmitting(false);
      onComplete?.();
      return;
    } else {
      // console.error(error);
      setIsSubmitting(false);
      setErrors(["Sorry, file upload failed.  Please try again."]);
    }
  };

  return (
    <>
      <form
        key={"newfileform"}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        style={{ width: "100%" }}
      >
        <Grid
          container
          spacing={2}
          style={{ width: "100%", minWidth: "400px" }}
          key={"newfilegridcontainer"}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
            key={"newfilegriditem1"}
          >
            <RenderFileUploadBox
              uploadedFiles={uploadedFiles}
              setErrors={setErrors}
              setUploadedFiles={setUploadedFiles}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
            key={"newfilegriditem2"}
          >
            <TextField
              key={"newfiletitle"}
              variant="outlined"
              label="Document Title"
              placeholder="Enter document title here"
              autoFocus={true}
              InputLabelProps={{ shrink: true }}
              value={title}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                event.preventDefault();
                event.stopPropagation();
                setTitle(event.target.value);
              }}
              sx={{
                width: "100%",
                marginBottom: "1rem",
                marginTop: "20px",
              }}
            />
            <DocumentTypeFormControl
              onFocus={() => setErrors([])}
              onChange={(documentTypeId:number) => {
                setDocumentType(documentTypeId)
              }}
              includeOnlyOptions={includeOnlyDocumentTypeIds}
              initialDocumentTypeId={documentType}
            />

            <TextField
              key={"newfilenotes"}
              variant="outlined"
              label="Notes"
              style={{ width: "100%" }}
              minRows={3}
              maxRows={10}
              inputProps={{ maxLength: 5000 }}
              value={notes}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                event.preventDefault();
                event.stopPropagation();
                setNotes(event.target.value);
              }}
              onFocus={() => setErrors([])}
              multiline={true}
            />
          </Grid>
        </Grid>
        {errors?.length
          ? errors.map((error: string) => (
              <Typography color="error" key={error} sx={{ m: 1 }}>
                {error}
              </Typography>
            ))
          : null}
        {progress && (
          <Box sx={{ width: "100%" }} key={"newfileprogressbox"}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="secondary"
              sx={{
                height: 16,
                mt: 2,
                mb: 1,
              }}
            />
          </Box>
        )}
        {isSubmitting && <Typography variant="body2">uploading</Typography>}
        <Box
          key={"newfilebuttonbox"}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            "& button": {
              mt: 1,
            },
          }}
        >
          <Button
            key="newfileuploadbutton"
            type="submit"
            color="secondary"
            variant="contained"
            autoFocus
            disabled={isSubmitting}
          >
            {isNew ? "Upload" : "Update"}
          </Button>
          <Button
            onClick={() => onCancel?.()}
            color="secondary"
            variant="outlined"
            key="newfilecancelbutton"
          >
            Cancel
          </Button>
        </Box>
      </form>
    </>
  );
};

const AcceptedFileFormats = () => {
  return (
    <Typography
      variant="body2"
      sx={{
        py: 2,
      }}
    >
      Accepted file formats (5mb size limit): .jpg, .png, .gif, .pdf, .doc
    </Typography>
  );
};

interface RenderProps {
  uploadedFiles?: FileList;
  handleChooseClick: () => void;
  previewImage?: JSX.Element;
}
export function RenderFileUploadArea(props: RenderProps): JSX.Element {
  const uploadedFiles = props.uploadedFiles;
  const uploadComponent = (
    <Box key={"previewImageBox1"}>
      {props.previewImage || <></>}
      <Typography
        variant="h4"
        color="secondary"
        sx={{
          pb: 2,
          fontWeight: 600,
        }}
      >
        Drag or upload a file here
      </Typography>
      <Button
        onClick={props.handleChooseClick}
        variant="contained"
        color="secondary"
      >
        Choose a file
      </Button>
      <AcceptedFileFormats />
    </Box>
  );
  if (!uploadedFiles || !uploadedFiles.item || uploadedFiles.length === 0) {
    return uploadComponent;
  }
  const file: File | null = uploadedFiles.item(0);
  if (!file) {
    return uploadComponent;
  }

  // We have a file
  if (file.type.indexOf("image/") === 0) {
    const objectUrl = URL.createObjectURL(file.slice());
    return (
      <Box key={"previewImageBox2"}>
        <img
          src={objectUrl}
          style={{ maxWidth: "100%", maxHeight: "300px", minHeight:"220px" }}
        />
        <Typography variant="h3">{file.name}</Typography>
      </Box>
    );
  } else if (file.type.indexOf("application/pdf") === 0) {
    const objectUrl = URL.createObjectURL(file) + "#toolbar=0";
    return (
      <Box key={"previewImageBox3"}>
        <object
          data={objectUrl}
          type="application/pdf"
          style={{
            border: "0",
            minWidth: "300px",
            minHeight: "300px",
            width: "100%",
            pointerEvents: "none",
          }}
        >
          <iframe
            src={objectUrl}
            style={{
              border: "0",
              minWidth: "300px",
              minHeight: "300px",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            This browser does not support PDFs. Please download the PDF to view
            it
          </iframe>
        </object>
        <Typography variant="h3">{file.name}</Typography>
      </Box>
    );
  }

  const extension = file?.type?.split("/")?.[1];
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxHeight: "400px",
      }}
      key={"previewImageBox4"}
    >
      <DocumentIcon extension={extension} />
      <Typography variant="h3">{file.name}</Typography>
    </Box>
  );
}

interface RenderFileUploadBoxInterface {
  uploadedFiles?: FileList;
  setErrors: (errs: string[]) => void;
  setUploadedFiles: (inputFiles: FileList) => void;
  previewImage?: JSX.Element;
}

export function RenderFileUploadBox(props: RenderFileUploadBoxInterface):JSX.Element {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // triggers the input when the button is clicked
  function handleChooseClick() {
    inputRef?.current?.click();
  }

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLFormElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    props.setErrors([]);
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // at least one file has been dropped so do something
      validateFile(e.dataTransfer.files);
    }
  };

  // triggers when file is selected with click
  const handleChange = function (e: ChangeEvent<HTMLInputElement>) {

    props.setErrors([]);
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      // at least one file has been selected so do something
      validateFile(e.target.files);
    }

  };

  const validateFile = async (inputFiles: FileList): Promise<void> => {
    if (inputFiles.length === 0) {
      props.setErrors(["Please drop or select a file."]);
      return;
    }

    // allow multiple?
    const errs: string[] = [];
    for (let i = 0; i < inputFiles.length; i++) {
      const file: File | null = inputFiles.item(i);
      if (file) {
        if (file.size > MAX_UPLOAD_BYTES) {
          console.error(file.size, ">", MAX_UPLOAD_BYTES);
          errs.push(`"${file.name}" is too large`);
        }
        if (!USER_DOC_UPLOAD_TYPES.includes(file.type)) {
          console.error(file.type, "!includes", USER_DOC_UPLOAD_TYPES);
          errs.push(`"${file.name}" file type: ${file.type} not accepted`);
        }
      } else {
        errs.push("no file");
      }
    }
    props.setUploadedFiles(inputFiles); // TODO: can they be added one at a time in the for loop above?
    if (errs?.length) {
      props.setErrors(errs);
    }
  };

  // TODO unify this coponent with the edituserdocument component, and remove this memoization (we don't need it on edit for some reason
  // but if we remove it from here then pdfs will re-render on every keypress)
  const filePreviewArea = React.useMemo(() => {
    return (
      <RenderFileUploadArea
        uploadedFiles={props.uploadedFiles}
        handleChooseClick={handleChooseClick}
        previewImage={props.previewImage}
      />
    );
  }, [props.uploadedFiles]);

  return (
    <Box
      sx={{
        my: 2,
        borderWidth: 2,
        borderRadius: 2,
        borderStyle: "dashed",
        borderColor: "secondary.light",
        backgroundColor: dragActive ? "white" : "success.light",
      }}
      onDragEnter={handleDrag}
    >
      <Box
        key={"boxthatcontainsfileinput"}
        style={{
          textAlign: "center",
          position: "relative",
        }}
      >
        <input
          type="file"
          ref={inputRef}
          id="input-file-upload"
          multiple={true}
          style={{ display: "none" }}
          onChange={handleChange}
        />
        <label
          id="label-file-upload"
          htmlFor="input-file-upload"
          style={{
            display: "flex",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 16px",
          }}
        >
          {dragActive && (
            <Box
              key={"newfiledragboxactive"}
              id="drag-file-element"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleChooseClick}
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: 0,
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            />
          )}
          {filePreviewArea}
        </label>
      </Box>
    </Box>
  );
}
